import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { playerService } from '../../../services/playerService';

const positionFilters = [
  { label: 'All', value: 'all' },
  { label: 'Point Guard', value: 'point_guard' },
  { label: 'Shooting Guard', value: 'shooting_guard' },
  { label: 'Small Forward', value: 'small_forward' },
  { label: 'Power Forward', value: 'power_forward' },
  { label: 'Center', value: 'center' },
];

const positionLabels = positionFilters.reduce((acc, pos) => {
  acc[pos.value] = pos.label;
  return acc;
}, {});

const statusStyles = {
  active: 'bg-success/10 text-success',
  injured: 'bg-warning/10 text-warning',
  suspended: 'bg-error/10 text-error',
  transferred: 'bg-muted-foreground/10 text-muted-foreground',
  inactive: 'bg-muted/50 text-muted-foreground',
};

const isNumericValue = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric);
};

const formatHeight = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (!isNumericValue(value)) {
    return typeof value === 'string' ? value : '-';
  }
  const cm = Number(value);
  const feet = Math.floor(cm / 30.48);
  const inches = Math.round((cm / 2.54) - (feet * 12));
  return `${feet}'${inches}" (${cm} cm)`;
};

const formatWeight = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (!isNumericValue(value)) {
    return typeof value === 'string' ? value : '-';
  }
  const kg = Number(value);
  const lbs = Math.round(kg * 2.20462);
  return `${kg} kg • ${lbs} lbs`;
};

const formatStatus = (status) => {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const getPlayerPhotoUrl = (player) => {
  if (player?.photoUrl && typeof player.photoUrl === 'string' && player.photoUrl.trim()) {
    if (!player.photoUrl.includes('no_image.png')) return player.photoUrl.trim();
  }
  return playerService.getPlayerPhotoUrl(player);
};

const hasViewablePhoto = (url) =>
  Boolean(url && typeof url === 'string' && url.trim() && !url.includes('no_image.png'));

const formatPositionLabel = (player) => {
  const raw = player?.playerPosition || player?.position;
  if (!raw || raw === 'other') return 'Flex';
  return positionLabels[raw] || String(raw).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * @param {object} props
 * @param {Array} props.players
 * @param {(player: object) => string | null | undefined} [props.getProfileHref] - when set, player names link to this path
 */
const TeamRoster = ({ players, getProfileHref }) => {
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [photoPreview, setPhotoPreview] = useState(null);

  const normalizedPlayers = useMemo(() => {
    if (!players) return [];
    return players.map((player) => {
      const photoUrl = getPlayerPhotoUrl(player);
      return {
        id: player?.id,
        name: player?.userProfile?.fullName || player?.name || 'Unknown Player',
        position: player?.playerPosition || player?.position || 'other',
        positionLabel: formatPositionLabel(player),
        jerseyNumber: player?.jerseyNumber,
        heightCm: player?.heightCm || player?.height,
        weightKg: player?.weightKg || player?.weight,
        photoUrl,
        hasPhoto: hasViewablePhoto(photoUrl),
        status: player?.playerStatus || player?.status || 'active',
        stats: player?.stats || player?.statistics || {},
        profileHref: typeof getProfileHref === 'function' ? getProfileHref(player) : null,
      };
    });
  }, [players, getProfileHref]);

  const filteredPlayers = useMemo(() => {
    if (selectedPosition === 'all') return normalizedPlayers;
    return normalizedPlayers.filter((player) => player.position === selectedPosition);
  }, [normalizedPlayers, selectedPosition]);

  const closePreview = () => setPhotoPreview(null);

  return (
    <div className="bg-card rounded-lg card-shadow p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-4 sm:mb-0">Team Roster</h2>

        <div className="flex flex-wrap gap-2">
          {positionFilters?.map((position) => (
            <Button
              key={position.value}
              variant={selectedPosition === position.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPosition(position.value)}
              className="text-xs"
            >
              {position.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredPlayers?.map((player) => (
          <div
            key={player?.id}
            className="bg-muted rounded-lg p-4 micro-interaction hover:shadow-lg transition-all border border-border/40"
          >
            <div className="flex items-center gap-3 mb-4">
              <button
                type="button"
                disabled={!player.hasPhoto}
                onClick={() =>
                  player.hasPhoto &&
                  setPhotoPreview({ url: player.photoUrl, name: player.name })
                }
                className={`w-14 h-14 rounded-full overflow-hidden border border-border flex items-center justify-center bg-background shrink-0 ${
                  player.hasPhoto
                    ? 'cursor-pointer hover:ring-2 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary'
                    : 'cursor-default'
                }`}
                title={player.hasPhoto ? 'View photo' : undefined}
                aria-label={player.hasPhoto ? `View photo of ${player.name}` : undefined}
              >
                {player.hasPhoto ? (
                  <Image
                    src={player.photoUrl}
                    alt={player?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={24} className="text-muted-foreground" />
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {player.profileHref ? (
                    <Link
                      to={player.profileHref}
                      className="font-semibold text-foreground truncate hover:text-primary hover:underline focus:outline-none focus:text-primary"
                      title={`Open ${player.name} profile`}
                    >
                      {player?.name}
                    </Link>
                  ) : (
                    <h3 className="font-semibold text-foreground truncate">{player?.name}</h3>
                  )}
                  {player?.jerseyNumber != null && player.jerseyNumber !== '' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-background border border-border text-muted-foreground shrink-0">
                      #{player.jerseyNumber}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{player?.positionLabel}</p>
              </div>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground mb-4">
              <div className="flex items-center gap-2">
                <Icon name="Ruler" size={12} className="text-muted-foreground" />
                <span>{formatHeight(player?.heightCm)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Weight" size={12} className="text-muted-foreground" />
                <span>{formatWeight(player?.weightKg)}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-foreground">{player?.stats?.ppg ?? '-'}</p>
                <p className="text-xs text-muted-foreground">PPG</p>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{player?.stats?.rpg ?? '-'}</p>
                <p className="text-xs text-muted-foreground">RPG</p>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{player?.stats?.apg ?? '-'}</p>
                <p className="text-xs text-muted-foreground">APG</p>
              </div>
            </div>

            <div className="mt-3">
              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  statusStyles[player?.status] || 'bg-muted-foreground/10 text-muted-foreground'
                }`}
              >
                {formatStatus(player?.status)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {filteredPlayers?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No players found for the selected position.</p>
        </div>
      )}

      {photoPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={closePreview}
          role="dialog"
          aria-modal="true"
          aria-label="Player photo"
        >
          <div
            className="relative max-w-lg w-full bg-card rounded-xl border border-border shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
              <p className="font-medium text-foreground truncate">{photoPreview.name}</p>
              <button
                type="button"
                onClick={closePreview}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                aria-label="Close photo"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="bg-muted flex items-center justify-center p-4 min-h-[280px]">
              <Image
                src={photoPreview.url}
                alt={photoPreview.name}
                className="max-h-[70vh] w-auto max-w-full object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamRoster;
